import React, { useState } from "react";
import { Row, Col } from "antd";
import SbTabs from "uiCore/SbTabs/SbTabs";
import ChartCard from "../components/ChartCard";
import RankedProgressBars from "../components/RankedProgressBars";
import FunctionAccordion from "./FunctionAccordion";
import { formatUsd, formatHours } from "../dashboardData";
import { PanelStack } from "../Dashboard.styles";

const TAB_ITEMS = [
  { key: "usd", label: "$ Value" },
  { key: "hrs", label: "Hours Saved" },
];

const flattenSubFunctions = (functions) => (functions || []).flatMap((f) => f.sub_function || []);

// canViewDollarValue defaults to false (fail closed) — a future caller that
// forgets to pass it hides $ figures rather than silently showing
// compensation-derived data.
const TotalPotentialPanel = ({ totalPotentialUsd, totalPotentialHours, canViewDollarValue = false }) => {
  // Always defaults to "usd" rather than seeding off canViewDollarValue —
  // entitlements can still be loading when this mounts, and isHrs below
  // already forces the hours fallback whenever canViewDollarValue is false
  // regardless of mode, so seeding "hrs" here only risked getting stuck
  // there if canViewDollarValue flipped true after mount.
  const [mode, setMode] = useState("usd");
  const isHrs = !canViewDollarValue || mode === "hrs";
  const data = (isHrs ? totalPotentialHours : totalPotentialUsd)?.breakdown;
  const formatter = isHrs ? formatHours : formatUsd;
  const noun = isHrs ? "Hours Saved" : "$ Value";

  if (!data) return null;

  const subFunctions = flattenSubFunctions(data.function);

  return (
    <PanelStack>
      {canViewDollarValue && <SbTabs activeKey={mode} onChange={setMode} items={TAB_ITEMS} />}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <ChartCard title="By Legal Entity" subtitle={`${noun} distribution across legal entities`}>
            <RankedProgressBars
              labels={data.legal_entity.map((r) => r.name)}
              values={data.legal_entity.map((r) => r.value)}
              valueFormatter={formatter}
            />
          </ChartCard>
        </Col>
        <Col xs={24} md={12}>
          <ChartCard title="By Business Unit" subtitle={`${noun} across each Business Unit`}>
            <RankedProgressBars
              labels={data.business_unit.map((r) => r.name)}
              values={data.business_unit.map((r) => r.value)}
              valueFormatter={formatter}
            />
          </ChartCard>
        </Col>
      </Row>

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} md={12}>
          <ChartCard title="By Function" subtitle={`${noun} per Function — click to expand Sub-Functions`}>
            <FunctionAccordion functions={data.function} formatter={formatter} />
          </ChartCard>
        </Col>
        <Col xs={24} md={12}>
          <ChartCard title="By Job Level" subtitle={`${noun} per seniority band`}>
            <RankedProgressBars
              labels={data.job_level.map((r) => r.name)}
              values={data.job_level.map((r) => r.value)}
              valueFormatter={formatter}
            />
          </ChartCard>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <ChartCard title="By Sub-Function" subtitle={`${noun} per Sub-Function`}>
            <RankedProgressBars
              labels={subFunctions.map((r) => r.name)}
              values={subFunctions.map((r) => r.value)}
              valueFormatter={formatter}
            />
          </ChartCard>
        </Col>
      </Row>
    </PanelStack>
  );
};

export default TotalPotentialPanel;
